//MonetizeForm.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import backendURL from "../../config";

// Reusable form component for adding or editing monetization data
export default function MonetizeForm({ mode, chatbotId,  initialData, onClose, onSaveSuccess }) {

  const formattedDate = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const defaultFormData = {
    title: "",
    days: 7, // Default value
    page_content: "",
    page_url: "",
    is_default: false,
    upload_date: formattedDate
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Effect to populate form when in 'edit' mode or reset for 'add' mode
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        id: initialData.id, // Keep the id for update requests
        title: initialData.title || "",
        days: initialData.days || 7,
        page_content: initialData.page_content || "",
        page_url: initialData.page_url || "",
        is_default: initialData.is_default || false,
      });
      setError(null); // Clear any previous errors
    } else {
      // Reset form for 'add' mode or if initialData is missing in 'edit' mode
      setFormData(defaultFormData);
      setError(null);
    }
  }, [mode, initialData]); // Rerun effect if mode or initial data changes

  // Handle input changes for all fields
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing/changing values
    if (error) {
      setError(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null); // Clear previous errors

    const token = localStorage.getItem("access_token");
    if (!token) {
        setError("Authentication token not found. Please log in.");
        setLoading(false);
        return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // Prepare data - ensure 'days' is a number if it comes from select as string
    const dataToSend = {
        ...formData,
        days: parseInt(formData.days, 10) || 7, // Ensure days is integer
        // NOTE: We assume the backend handles 'upload_date' on creation and 'expired_date' calculation.
        // If you need to send upload_date from frontend on ADD:
        // ...(mode === 'add' && { upload_date: new Date().toISOString().slice(0, 19).replace('T', ' ') })
    };

    // Determine API endpoint and method
    const isEditMode = mode === 'edit';
    const url = isEditMode
      ? `${backendURL}/update-monetize/${formData.id}` // Endpoint for updating
      : `${backendURL}/add-monetize`; // Endpoint for adding

    // Use POST for both add and update as per original code structure
    // (If backend uses PUT for update, change method accordingly)
    const method = 'post';

    try {

      
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      console.log(`Submitting (${mode}) to ${url} with data:`, dataToSend);
      const response = await axios({ method, url, headers, params, data: dataToSend });

      console.log("API response:", response.data);
      setLoading(false);
      onSaveSuccess(); // Trigger refresh in parent component
      onClose(); // Close the modal

    } catch (err) {
      console.error("Error submitting form:", err.response || err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || "An unexpected error occurred.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null); // Clear error on cancel
    onClose(); // Close the modal without saving
  };

  const formTitle = mode === 'edit' ? 'Edit Advertisement' : 'Add New Advertisement';

  return (
    // Add padding and structure for the modal content
    <div > {/* Reduced padding slightly if modal has its own */}
      <h4 className="mb-3">{formTitle}</h4> {/* Modal title */}
      <form onSubmit={handleSubmit}>
        {/* Error Display Area */}
        {error && (
          <div className="alert alert-danger  bg-danger text-white" role="alert">
            {error}
          </div>
        )}

        <div className="row g-3"> {/* 'g-3' adds gutters between columns */}
          {/* Title Field */}
          <div className="col-md-8"> {/* Span 8 columns on medium screens and up */}
            <label htmlFor="title" className="form-label">Title <span className="text-danger">*</span></label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          {/* Days Field */}
          <div className="col-md-4"> {/* Span 4 columns */}
            <label htmlFor="days" className="form-label">Display Duration (Days) <span className="text-danger">*</span></label>
            <select
              id="days"
              name="days"
              className="form-select" // Use form-select for Bootstrap styling
              value={formData.days}
              onChange={handleChange}
              disabled={loading}
            >
              <option value={7}>7 Days</option>
              <option value={15}>15 Days</option>
              <option value={30}>30 Days</option>
            </select>
          </div>

          {/* Page Content Field */}
          <div className="col-12"> {/* Full width */}
            <label htmlFor="page_content" className="form-label">Advertisement Details <span className="text-danger">*</span></label>
            <textarea
              id="page_content"
              name="page_content"
              className="form-control"
              rows="4" // Reduced rows slightly
              value={formData.page_content}
              onChange={handleChange}
              disabled={loading}
            ></textarea>
          </div>

          {/* Page URL Field */}
          <div className="col-12"> {/* Full width */}
            <label htmlFor="page_url" className="form-label">Target URL (Starts with http:// or https://) <span className="text-danger">*</span></label>
            <input
              type="url" // Use type="url" for basic validation
              id="page_url"
              name="page_url"
              placeholder="https://example.com"
              className="form-control"
              value={formData.page_url}
              onChange={handleChange}
              pattern="https?://.*" // Basic pattern check
              title="Please enter a valid URL starting with http:// or https://"
              disabled={loading}
            />
          </div>

          {/* Featured Field (Using Switch) */}
          <div className="col-12">
            <div className="form-check form-switch mt-2">
              <input
                type="checkbox"
                id="is_default"
                name="is_default"
                className="form-check-input"
                role="switch"
                checked={formData.is_default}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="is_default" className="form-check-label">
                Set as Featured Advertisement?
              </label>
            </div>
          </div>
        </div> {/* End row */}

        {/* Action Buttons */}
        <div className="d-flex justify-content-end mt-4"> {/* Align buttons to the right */}
          <button
            type="button"
            className="btn btn-secondary me-2" // Margin end for spacing
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              mode === 'edit' ? 'Save Changes' : 'Add Advertisement' // Dynamic button text
            )}
          </button>
        </div>
      </form>
    </div>
  );
}